import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { MessageSquare, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { usePageMeta } from "../lib/meta";
import { Reveal } from "../components/Chrome";

export default function Community() {
  usePageMeta("Community — Klyx™", "An open room between the public and the Klyx™ studio.");
  const qc = useQueryClient();
  const [name, setName] = useState(() => localStorage.getItem("klyx_name") || "");
  const [message, setMessage] = useState("");
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const { data: content } = useQuery({
    queryKey: ["content", "community"],
    queryFn: async () => (await api.get("/content/community")).data,
  });
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["community"],
    queryFn: async () => (await api.get("/community")).data,
  });

  const postMutation = useMutation({
    mutationFn: async () => (await api.post("/community", { name: name.trim(), message: message.trim() })).data,
    onSuccess: () => {
      localStorage.setItem("klyx_name", name.trim());
      setMessage("");
      qc.invalidateQueries({ queryKey: ["community"] });
      toast.success("Posted to the Commons");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const replyMutation = useMutation({
    mutationFn: async (postId) =>
      (await api.post(`/community/${postId}/replies`, { name: name.trim() || "Visitor", message: replyText.trim() })).data,
    onSuccess: () => {
      localStorage.setItem("klyx_name", name.trim());
      setReplyText("");
      setReplyFor(null);
      qc.invalidateQueries({ queryKey: ["community"] });
      toast.success("Reply added");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const c = content?.data || { title: "The Commons", sub: "", image: "" };

  return (
    <div data-testid="community-page" className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-mauve mb-6">Open room</p>
              <h1 data-testid="community-title" className="font-heading font-black text-4xl sm:text-6xl tracking-tighter text-silver">
                {c.title}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-zinc-200 leading-relaxed max-w-xl">{c.sub}</p>
            </Reveal>
          </div>
          {c.image && (
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="aspect-[16/9] overflow-hidden border border-white/10">
                <img src={c.image} alt="Community" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* New post */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 border border-white/10 bg-surface p-8">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-mauve mb-6 flex items-center gap-2">
                <MessageSquare size={14} /> Start a thread
              </p>
              <input
                data-testid="community-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-ink border border-white/10 px-4 py-3 text-sm text-silver placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-mauve mb-4"
              />
              <textarea
                data-testid="community-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask the studio anything…"
                rows={5}
                className="w-full bg-ink border border-white/10 px-4 py-3 text-sm text-silver placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-mauve resize-none"
              />
              <button
                data-testid="community-post-button"
                disabled={!name.trim() || !message.trim() || postMutation.isPending}
                onClick={() => postMutation.mutate()}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-mauve text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold transition-[background-color,transform] duration-200 hover:bg-mauve-dark disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send size={14} /> {postMutation.isPending ? "Posting…" : "Post to the Commons"}
              </button>
            </div>
          </div>

          {/* Threads */}
          <div className="lg:col-span-8 space-y-6">
            {isLoading && <p className="text-zinc-300 text-sm">Loading threads…</p>}
            {!isLoading && posts.length === 0 && (
              <div data-testid="community-empty" className="border border-dashed border-white/15 p-12 text-center text-zinc-300 text-sm">
                No threads yet — be the first to say hello.
              </div>
            )}
            {posts.map((p) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid={`community-post-${p.id}`}
                className="border border-white/10 bg-surface p-8"
              >
                <div className="flex items-baseline justify-between gap-4 mb-3">
                  <p className="font-heading font-bold text-silver">{p.name}</p>
                  <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-400">
                    {new Date(p.created_at).toLocaleDateString()}
                  </p>
                </div>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed whitespace-pre-wrap">{p.message}</p>
                {p.replies?.length > 0 && (
                  <div className="mt-6 space-y-4 border-l-2 border-mauve/30 pl-6">
                    {p.replies.map((r) => (
                      <div key={r.id} data-testid={`community-reply-${r.id}`}>
                        <p className="flex items-center gap-2 text-xs font-medium text-zinc-200 mb-1">
                          {r.name}
                          {r.is_staff && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-widest uppercase text-mauve border border-mauve/40 px-2 py-0.5">
                              <ShieldCheck size={10} /> Klyx™ staff
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{r.message}</p>
                      </div>
                    ))}
                  </div>
                )}
                {replyFor === p.id ? (
                  <div className="mt-6">
                    <textarea
                      data-testid={`reply-input-${p.id}`}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={3}
                      placeholder="Write a reply…"
                      className="w-full bg-ink border border-white/10 px-4 py-3 text-sm text-silver placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-mauve resize-none"
                    />
                    <div className="mt-3 flex gap-3">
                      <button
                        data-testid={`reply-submit-${p.id}`}
                        disabled={!replyText.trim() || replyMutation.isPending}
                        onClick={() => replyMutation.mutate(p.id)}
                        className="bg-mauve text-white px-5 py-2 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 disabled:opacity-40"
                      >
                        Reply
                      </button>
                      <button
                        data-testid={`reply-cancel-${p.id}`}
                        onClick={() => { setReplyFor(null); setReplyText(""); }}
                        className="border border-white/15 text-zinc-200 px-5 py-2 text-[11px] tracking-[0.2em] uppercase hover:text-white transition-colors duration-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    data-testid={`reply-open-${p.id}`}
                    onClick={() => setReplyFor(p.id)}
                    className="mt-6 text-[11px] font-mono tracking-[0.2em] uppercase text-mauve hover:text-white transition-colors duration-200"
                  >
                    Reply →
                  </button>
                )}
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
